import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { createDealerUser } from "../../../services/users";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiUsers,
  FiMail,
  FiPhone,
  FiMapPin,
  FiSettings,
  FiBell,
  FiSave,
  FiLock,
} from "react-icons/fi";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import Lozenge from "../../../components/BaseUI/Lozenge";
import Label from "../../../components/BaseUI/Label";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const CreateDealer = () => {
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(currentUser.role, "/dealers", currentUser.pages || [], !!currentUser.fullAccess);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);

  const [dealerData, setDealerData] = useState({
    name: "",
    code: "",
    email: "",
    phone: "",
    address: "",
    status: "active",
    settings: {
      notificationEmail: "",
      supportPhone: "",
      timezone: "Europe/Istanbul",
      language: "tr",
    },
  });

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.DEALERS"), url: "/dealers" },
    { label: translate("UI.ADD") },
  ];

  const handleInputChange = (field, value) => {
    // Bayi kodu için otomatik büyük harf
    if (field === "code") {
      value = value.toUpperCase();
    }

    if (field.includes(".")) {
      const [section, subField] = field.split(".");
      setDealerData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subField]: value,
        },
      }));
    } else {
      setDealerData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const validateForm = () => {
    const errors = [];

    // Zorunlu alanları kontrol et
    const requiredFields = ["name", "code", "email", "phone", "address"];
    const missingFields = requiredFields.filter((field) => !dealerData[field]);

    if (missingFields.length > 0) {
      errors.push(
        `Lütfen tüm zorunlu alanları doldurun: ${missingFields
          .map((field) => translate(`UI.${field.toUpperCase()}`))
          .join(", ")}`,
      );
    }

    // Email formatını kontrol et
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (dealerData.email && !emailRegex.test(dealerData.email)) {
      errors.push("Geçersiz e-posta adresi formatı");
    }

    // Bayi kodu minimum uzunluk kontrolü
    if (dealerData.code && dealerData.code.length < 3) {
      errors.push("Bayi kodu en az 3 karakter olmalıdır");
    }

    return errors;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Form validasyonu
      const errors = validateForm();
      if (errors.length > 0) {
        setToasts([
          {
            description: errors.join("\n"),
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/dealers`,
        {
          ...dealerData,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description: response.data.message || "Bayi başarıyla oluşturuldu.",
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);

        setTimeout(() => {
          navigate("/dealers");
        }, 1500);
      }
    } catch (error) {
      console.error("Bayi oluşturma hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            "Bayi oluşturulurken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  if (!canWrite) {
    return (
      <AppPage title={translate("UI.ADD_DEALER")} breadcrumbs={breadcrumbs}>
        <Container>
          <ContainerItem>
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>Bu sayfada değişiklik yapma yetkiniz yok.</span>
              <Button
                appearance="primary-animated"
                onClick={() => navigate("/dealers")}
              >
                Geri Dön
              </Button>
            </div>
          </ContainerItem>
        </Container>
      </AppPage>
    );
  }

  return (
    <AppPage title={translate("UI.ADD_DEALER")} breadcrumbs={breadcrumbs}>
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card shadow-sm">
            <div className="card-body">
              {/* Bayi Bilgileri */}
              <div className="section-container">
                <div className="section-header">
                  <div className="section-icon">
                    <FiUsers className="text-primary" />
                  </div>
                  <div className="section-title">
                    <h5 className="mb-1">
                      {translate("UI.DEALER_INFORMATION")}
                    </h5>
                    <p className="text-muted small mb-0">
                      {translate("UI.DEALER_INFO_DESC")}
                    </p>
                  </div>
                </div>

                <div className="section-content">
                  <div className="d-flex flex-wrap gap-3">
                    <div className="w-45">
                      <Input
                        type="text"
                        label={translate("UI.NAME")}
                        value={dealerData.name}
                        onChange={(value) => handleInputChange("name", value)}
                        required
                        prefix={<FiUsers className="text-muted" />}
                        hint={translate("UI.DEALER_NAME_DESC")}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="text"
                        label={translate("UI.CODE")}
                        value={dealerData.code}
                        onChange={(value) => handleInputChange("code", value)}
                        required
                        hint={translate("UI.DEALER_CODE_DESC")}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="email"
                        label="Email"
                        value={dealerData.email}
                        onChange={(value) => handleInputChange("email", value)}
                        required
                        prefix={<FiMail className="text-muted" />}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="tel"
                        label={translate("UI.PHONE")}
                        value={dealerData.phone}
                        onChange={(value) => handleInputChange("phone", value)}
                        required
                        prefix={<FiPhone className="text-muted" />}
                      />
                    </div>
                    <div className="w-90">
                      <Input
                        type="text"
                        label={translate("UI.ADDRESS")}
                        value={dealerData.address}
                        onChange={(value) =>
                          handleInputChange("address", value)
                        }
                        required
                        prefix={<FiMapPin className="text-muted" />}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Ayarlar */}
              <div className="section-container">
                <div className="section-header">
                  <div className="section-icon">
                    <FiSettings className="text-info" />
                  </div>
                  <div className="section-title">
                    <h5 className="mb-1">{translate("UI.SETTINGS")}</h5>
                    <p className="text-muted small mb-0">
                      {translate("UI.DEALER_SETTINGS_DESC")}
                    </p>
                  </div>
                </div>

                <div className="section-content">
                  <div className="d-flex flex-wrap gap-3">
                    <div className="w-45">
                      <Input
                        type="email"
                        label={translate("UI.NOTIFICATION_EMAIL")}
                        value={dealerData.settings.notificationEmail}
                        onChange={(value) =>
                          handleInputChange("settings.notificationEmail", value)
                        }
                        prefix={<FiBell className="text-muted" />}
                        hint={translate("UI.NOTIFICATION_EMAIL_DESC")}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="tel"
                        label={translate("UI.SUPPORT_PHONE")}
                        value={dealerData.settings.supportPhone}
                        onChange={(value) =>
                          handleInputChange("settings.supportPhone", value)
                        }
                        prefix={<FiPhone className="text-muted" />}
                        hint={translate("UI.SUPPORT_PHONE_DESC")}
                      />
                    </div>
                    <div className="w-45">
                      <Label>{translate("UI.STATUS")}</Label>
                      <div className="form-control-container">
                        <Dropdown
                          trigger={
                            <div className="d-flex align-items-center justify-content-between p-2">
                              <Lozenge
                                appearance={
                                  dealerData.status === "active"
                                    ? "success-subtle"
                                    : "danger-subtle"
                                }
                              >
                                {translate(
                                  `UI.${dealerData.status.toUpperCase()}`,
                                )}
                              </Lozenge>
                            </div>
                          }
                        >
                          <DropdownContent>
                            <DropdownItem
                              onClick={() =>
                                handleInputChange("status", "active")
                              }
                              active={dealerData.status === "active"}
                            >
                              <Lozenge appearance="success-subtle">
                                {translate("UI.ACTIVE")}
                              </Lozenge>
                            </DropdownItem>
                            <DropdownItem
                              onClick={() =>
                                handleInputChange("status", "inactive")
                              }
                              active={dealerData.status === "inactive"}
                            >
                              <Lozenge appearance="danger-subtle">
                                {translate("UI.INACTIVE")}
                              </Lozenge>
                            </DropdownItem>
                          </DropdownContent>
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Butonlar */}
              <div className="form-actions p-3">
                <Button
                  appearance="danger-subtle"
                  onClick={() => navigate("/dealers")}
                >
                  {translate("UI.CANCEL")}
                </Button>
                <Button
                  appearance="primary-animated"
                  onClick={handleSubmit}
                  loading={loading}
                  icon={<FiSave />}
                >
                  {translate("UI.SAVE")}
                </Button>
              </div>
            </div>
          </div>
        </ContainerItem>
      </Container>

      <style>
        {`
                .section-container {
                    margin-bottom: 2rem;
                    padding: 1.5rem;
                    background: #fff;
                    border-radius: 8px;
                    border: 1px solid #e9ecef;
                }

                .section-container:last-child {
                    margin-bottom: 0;
                }

                .section-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .section-icon {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--bs-primary-bg-subtle);
                    border-radius: 8px;
                }

                .section-icon svg {
                    width: 24px;
                    height: 24px;
                }

                .section-title h5 {
                    font-size: 1.1rem;
                    margin: 0;
                    color: var(--bs-gray-900);
                }

                .section-content {
                    padding: 0 0.5rem;
                }

                .form-control-container {
                    background: #fff;
                    border: 1px solid #e2e5e8;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                }

                .form-control-container:hover {
                    border-color: var(--bs-primary);
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    margin-top: 2rem;
                    padding-top: 1rem;
                    border-top: 1px solid #e9ecef;
                }

                @media (max-width: 768px) {
                    .section-container {
                        padding: 1rem;
                    }

                    .section-header {
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                        gap: 0.5rem;
                    }

                    .section-icon {
                        width: 32px;
                        height: 32px;
                    }

                    .section-icon svg {
                        width: 20px;
                        height: 20px;
                    }
                }
                `}
      </style>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            // actions={[{ content: "Kapat", onClick: () => removeToast(index) }]}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(CreateDealer, ["admin"]);
